import moment from 'moment';
import _ from 'lodash';
import { useEffect } from 'react';
import { managerAPI } from '@/shared/api';
import {
  KEY_CANCEL,
  KEY_RECOIL,
  LIST_EVENT_SKIP_ON_RUN_EVENT,
  LIST_RUN_EVENT,
  PREVENT_AFIB_OVERRIDE_EVENT_LIST,
  RHYTHM_EVENT_LIST_CAN_EDIT,
  TYPE_BEAT_ENUM,
  TYPE_BEAT_EVENT_ENUM,
  TYPE_RHYTHM_EVENT_ENUM,
  typesPersistEvent,
} from '@/shared/config';
import { convertToMoment } from '@/shared/lib';
import { getEventDuration, getEventDurationInSecond } from '@/shared/lib/aiUtils';
import { useMergeState, useUpdateEffect } from '@/shared/lib/customHooks';
import { toastrError } from '@/shared/lib/toastNotification';
import {
  ADD_AFIB_TO_ARTIFACT_ERROR_MESSAGE,
  ADD_ARTIFACT_TO_AFIB_ERROR_MESSAGE,
  ADD_EVENT_AFIB_SHORT_DURATION_ERROR_MESSAGE,
  ADD_EVENT_NO_BEAT_ERROR_MESSAGE,
  CHANGE_EVENT_SAME_DURATION_ERROR_MESSAGE,
  UPDATE_EVENT_NO_DATA_ERROR_MESSAGE,
  UPDATE_EVENT_WITH_NEW_RANGE_ERROR_MESSAGE,
  generateUpdateEventInOtherEventErrorMessage,
  getBeatCountAddUpdateEvents,
  getEventBothSideAddUpdateEvents,
  isCheckContinueData,
  updateEventChangeAndNew,
} from '@/entities/event';
import { NO_BEATS_LOW_DURATION_ARTIFACT_ERROR_MESSAGE } from '@/widgets/beats/ui/handlerConstants';
import { logError } from '@/shared/lib/apiHelpers';
import { getDataRange, isRangesOverlap } from '@/shared/lib/timeRange';
import {
  selectActiveTab,
  selectBeatChanges,
  selectControlModal,
  selectEventChanges,
  selectEventNews,
  selectPickedEndDate,
  selectPickedStartDate,
  selectSelectedStrip,
  useReportStore,
} from '@/entities/report';
import type { ChangeEventDurationProps } from './changeEventDuration';

/**
 * Drives the modal that moves an ECG event's start and stop boundaries on the beats tab. Wires tab-scoped report-store reads and setters for event changes, new events, beat changes and the selected strip, holds the four date and time pickers in merged local state, and exposes validate-then-persist handlers for saving and cancelling.
 *
 * @param props - Modal props carrying the selected event, the ECG data map, study and profile ids, the timezone offset and the onCancel callback.
 * @returns An object carrying state plus the onChange, onClickCancel and onClickSave handlers.
 */
const useChangeEventDuration = ({
  onCancel,
  timezone,
  timezoneOffset,
  ecgDataMap,
  studyId,
  profileId,
  selectedEvent,
}: ChangeEventDurationProps) => {
  const keyRecoil = KEY_RECOIL.BEAT_HR;

  const setEventNewsAction = useReportStore((s) => s.actions.setEventNews);
  /**
   * Writes the locally added events for the beats tab back into the report store. Forwards the value or updater to the store action with the tab key already bound.
   *
   * @param v - Replacement list of new events, or an updater receiving the current one.
   * @returns Nothing.
   */
  const setEventNews = (v: Parameters<typeof setEventNewsAction>[1]) =>
    setEventNewsAction(keyRecoil, v);
  /**
   * Reads the locally added ECG events for the beats tab out of the report store. Applies the tab-scoped selectEventNews selector to a fresh store snapshot instead of subscribing to it.
   *
   * @returns The list of newly created events awaiting save.
   */
  const getEventNews = () => selectEventNews(keyRecoil)(useReportStore.getState());
  const setEventChangesAction = useReportStore((s) => s.actions.setEventChanges);
  /**
   * Writes the pending event edits for the beats tab back into the report store. Forwards the value or updater to the store action with the tab key already bound.
   *
   * @param v - Replacement list of edited events, or an updater receiving the current one.
   * @returns Nothing.
   */
  const setEventChanges = (v: Parameters<typeof setEventChangesAction>[1]) =>
    setEventChangesAction(keyRecoil, v);
  /**
   * Reads the pending ECG event edits for the beats tab out of the report store. Applies the tab-scoped selectEventChanges selector to a fresh store snapshot instead of subscribing to it.
   *
   * @returns The list of edited events awaiting save.
   */
  const getEventChanges = () => selectEventChanges(keyRecoil)(useReportStore.getState());
  /**
   * Reads the pending beat edits for the beats tab out of the report store. Applies the tab-scoped selectBeatChanges selector to a fresh store snapshot instead of subscribing to it.
   *
   * @returns The list of pending beat changes for the beats tab.
   */
  const getBeatChanges = () => selectBeatChanges(keyRecoil)(useReportStore.getState());
  /**
   * Reads the strip currently selected on the beats tab out of the report store. Applies the tab-scoped selectSelectedStrip selector to a fresh store snapshot, so the value may be an event, a beat or an ECG bookmark.
   *
   * @returns The selected event, beat or ECG bookmark.
   */
  const getSelectedStrip = () => selectSelectedStrip(keyRecoil)(useReportStore.getState());
  const setSelectedStripAction = useReportStore((s) => s.actions.setSelectedStrip);
  /**
   * Writes the beats tab's selected strip back into the report store. Forwards the value or updater to the store action with the tab key already bound.
   *
   * @param v - Replacement event, beat or ECG bookmark, or an updater receiving the current one.
   * @returns Nothing.
   */
  const setSelectedStrip = (v: Parameters<typeof setSelectedStripAction>[1]) =>
    setSelectedStripAction(keyRecoil, v);
  const pickedStartDate = useReportStore(selectPickedStartDate(keyRecoil));
  const pickedEndDate = useReportStore(selectPickedEndDate(keyRecoil));
  const controlModal = useReportStore(selectControlModal(keyRecoil));
  const activeButton = useReportStore(selectActiveTab(keyRecoil));
  const setEventOptionsAction = useReportStore((s) => s.actions.setEventOptions);
  /**
   * Records an event-option snapshot for the beats tab so the duration edit can be undone and redone as one step. Forwards the value or updater to the store action with the tab key already bound.
   *
   * @param v - Replacement options payload, or an updater receiving the current one.
   * @returns Nothing.
   */
  const setEventOptions = (v: Parameters<typeof setEventOptionsAction>[1]) =>
    setEventOptionsAction(keyRecoil, v);

  const [state, setState] = useMergeState<{
    startDate: any;
    startTime: any;
    endDate: any;
    endTime: any;
    errorObject: any;
    isSaving: boolean;
  }>({
    startDate: undefined,
    startTime: undefined,
    endDate: undefined,
    endTime: undefined,
    errorObject: {},
    isSaving: false,
  });

  /**
   * Records one date or time picker value for the event duration being edited. Writes the incoming moment under the given field name and clears every previously reported validation error.
   *
   * @param name - Local state field the picker writes to, such as startDate or endTime.
   * @param time - Picked moment value for that field.
   * @param timeString - Formatted text emitted by the picker, accepted but unused.
   * @returns Nothing.
   */
  const onChange = (name: string, time: any, timeString?: string | string[]) => {
    setState({ [name]: time, errorObject: {} });
  };

  /**
   * Abandons the duration edit and closes the modal. Clears the validation errors and the saving flag, then invokes the onCancel callback supplied by the parent.
   *
   * @returns Nothing.
   */
  const onClickCancel = () => {
    setState({ errorObject: {}, isSaving: false });
    onCancel();
  };

  /**
   * Rejects a duration edit by surfacing the failure and marking both time inputs invalid. Fires an error toast with the given message, then clones the current error map, stamps startTime and endTime as invalid values and clears the saving flag.
   *
   * @param toastMessage - Error text shown in the toast; defaults to an empty string.
   * @returns Nothing.
   */
  const handleSetError = (toastMessage: string = '') => {
    toastrError(toastMessage, 'Error');
    const clonedErrorObject = { ...state.errorObject };
    _.assign(clonedErrorObject, {
      startTime: 'Invalid value',
      endTime: 'Invalid value',
    });
    setState({ errorObject: clonedErrorObject, isSaving: false });
  };

  /**
   * Applies the picked start and stop to the selected event once every ECG data and beat constraint holds. Snaps the range to available data, rejects it on no data, discontinuity, an AFib run under five seconds or fewer than two merged beats, resolves overlaps against fetched neighbouring events by merging, trimming or deleting them, then commits the result into event changes, new events and the selected strip before closing the modal.
   *
   * @returns A promise resolving once the edit is committed to the store and the modal closed, or an error toast has been raised.
   */
  const handleChangeEventDuration = async () => {
    try {
      const updatedEvent = _.cloneDeep(selectedEvent);
      const newStart = pickedStartDate || updatedEvent.start;
      const newStop = pickedEndDate || updatedEvent.stop;
      const momentNewStart = moment(newStart);
      const momentNewStop = moment(newStop);
      //*  khi mà change duration của 1 event mà khoảng thời gian mới không dính tới khoảng thời gian của event hiện tại thì nó không được update.
      const canChangeDuration =
        moment(updatedEvent.start).valueOf() <= momentNewStop.valueOf() &&
        moment(updatedEvent.stop).valueOf() >= momentNewStart.valueOf();
      if (!canChangeDuration) {
        handleSetError(UPDATE_EVENT_WITH_NEW_RANGE_ERROR_MESSAGE);
        return;
      }
      _.assign(updatedEvent, {
        start: newStart,
        stop: newStop,
      });
      const dataRange = getDataRange(ecgDataMap, momentNewStart, momentNewStop);
      if (_.isEmpty(dataRange)) {
        handleSetError(UPDATE_EVENT_NO_DATA_ERROR_MESSAGE);
        return;
      }
      const startData = dataRange[0].start;
      const stopData = dataRange[dataRange.length - 1].stop;
      if (startData > momentNewStart) {
        updatedEvent.start = moment(startData).toISOString();
      }
      if (stopData < momentNewStop) {
        updatedEvent.stop = moment(stopData).toISOString();
      }
      if (
        !isCheckContinueData(dataRange, updatedEvent) &&
        updatedEvent.type !== TYPE_RHYTHM_EVENT_ENUM.AFIB
      ) {
        handleSetError(UPDATE_EVENT_NO_DATA_ERROR_MESSAGE);
        return;
      }
      // Qui định khi edit event -> giống add event, xem event đang edit là event mới
      const localBeatChanges = getBeatChanges().filter(
        (beat) => beat.isLocalBeat || beat.type === TYPE_BEAT_ENUM.DELETED,
      );
      const newEventStart = moment(updatedEvent.start).valueOf();
      const newEventStop = moment(updatedEvent.stop).valueOf();
      const updateEventDuration = getEventDurationInSecond(updatedEvent.start, updatedEvent.stop);

      if (updatedEvent.type === TYPE_RHYTHM_EVENT_ENUM.AFIB) {
        if (updateEventDuration < 5) {
          handleSetError(ADD_EVENT_AFIB_SHORT_DURATION_ERROR_MESSAGE);
          return;
        }
      }

      const { mergeBeatCount, startBeat, stopBeat, diffBeatBySecond } =
        await getBeatCountAddUpdateEvents({
          start: updatedEvent.start,
          stop: updatedEvent.stop,
          samplingFrequency: ecgDataMap.samplingFrequency,
          localBeatChanges,
          ecgDataMap,
        });
      if (updatedEvent.type === TYPE_RHYTHM_EVENT_ENUM.AFIB && diffBeatBySecond < 5) {
        handleSetError(ADD_EVENT_AFIB_SHORT_DURATION_ERROR_MESSAGE);
        return;
      }
      if (
        updatedEvent.type !== TYPE_RHYTHM_EVENT_ENUM.ARTIFACT ||
        (updateEventDuration < 10 && updatedEvent?.type === TYPE_RHYTHM_EVENT_ENUM.ARTIFACT)
      ) {
        //* Can change artifact if no beat
        //* Check 1 beat
        if (mergeBeatCount < 2 && updatedEvent.type !== TYPE_RHYTHM_EVENT_ENUM.ARTIFACT) {
          handleSetError(ADD_EVENT_NO_BEAT_ERROR_MESSAGE);
          return;
        }
        if (mergeBeatCount < 2 && updatedEvent.type === TYPE_RHYTHM_EVENT_ENUM.ARTIFACT) {
          handleSetError(NO_BEATS_LOW_DURATION_ARTIFACT_ERROR_MESSAGE);
          return;
        }
      }
      if (
        moment(selectedEvent.start).valueOf() === startBeat &&
        moment(selectedEvent.stop).valueOf() === stopBeat
      ) {
        // show error when the duration of the event is the same
        handleSetError(CHANGE_EVENT_SAME_DURATION_ERROR_MESSAGE);
        return;
      }
      if (startBeat && startBeat !== stopBeat) {
        updatedEvent.start = moment(startBeat).toISOString();
        updatedEvent.stop = moment(stopBeat).toISOString();
      }
      // fetch event to check
      const startSearchDate = updatedEvent.start;
      const stopSearchDate = updatedEvent.stop;
      // typesPersistEvent is typed TYPE_RHYTHM_EVENT_ENUM[], but SVE_RUN/VE_RUN are TYPE_BEAT_EVENT_ENUM - pre-existing.
      const typesPersistEventClone: any[] = _.cloneDeep(typesPersistEvent);
      if (updatedEvent.type === TYPE_RHYTHM_EVENT_ENUM.SVT) {
        typesPersistEventClone.push(TYPE_BEAT_EVENT_ENUM.SVE_RUN);
      }
      if (updatedEvent.type === TYPE_RHYTHM_EVENT_ENUM.VT) {
        typesPersistEventClone.push(TYPE_BEAT_EVENT_ENUM.VE_RUN);
      }
      const holterEventsFilter = {
        studyId,
        profileId,
        types: typesPersistEventClone,
        startSearchDate,
        stopSearchDate,
      };
      // const { events } = await fetchHolterEvents(holterEventsFilter, 0, false, KEY_CANCEL.API_MODAL, true);
      const dataFormat = await getEventBothSideAddUpdateEvents(holterEventsFilter);
      const combinedEvents = updateEventChangeAndNew(
        dataFormat,
        getEventChanges(),
        getEventNews(),
        holterEventsFilter.startSearchDate,
        holterEventsFilter.stopSearchDate,
        typesPersistEventClone,
      );
      //
      const linkEvents: any[] = [];
      let errorEvent;
      let errorMessages;
      const combinedEventsLength = combinedEvents.length;
      for (let i = 0; i < combinedEventsLength; i += 1) {
        const event = combinedEvents[i];
        const eventType = event.type === TYPE_BEAT_ENUM.DELETED ? event.oldType : event.type;
        if (event.idEvent !== updatedEvent.idEvent) {
          if (
            PREVENT_AFIB_OVERRIDE_EVENT_LIST.includes(eventType) &&
            updatedEvent.type === TYPE_RHYTHM_EVENT_ENUM.AFIB
          ) {
            continue;
          }
          if (
            PREVENT_AFIB_OVERRIDE_EVENT_LIST.includes(updatedEvent.type) &&
            eventType === TYPE_RHYTHM_EVENT_ENUM.AFIB
          ) {
            continue;
          }
          const eventStart = moment(event.start).valueOf();
          const eventStop = moment(event.stop).valueOf();
          if (isRangesOverlap(eventStart, eventStop, newEventStart, newEventStop)) {
            const isSameType = eventType === updatedEvent.type;
            const isNewEventContainOldEvent =
              eventStart >= newEventStart && eventStop <= newEventStop;
            const isNewEventInsideOldEvent =
              newEventStart >= eventStart && newEventStop <= eventStop;
            if (isSameType) {
              //* Mới chứa cũ cùng loại
              if (isNewEventContainOldEvent) {
                console.log(
                  '[beatHr/changeEventDuration]-OVERLAP-sametype-isNewEventContainOldEvent',
                );
                if (
                  eventType === TYPE_RHYTHM_EVENT_ENUM.AFIB ||
                  eventType === TYPE_RHYTHM_EVENT_ENUM.ARTIFACT
                ) {
                  linkEvents.push(event);
                }
              } else if (isNewEventInsideOldEvent) {
                //* Mới trong cũ cùng loại
                console.log(
                  '[beatHr/changeEventDuration]-OVERLAP-sametype-isNewEventInsideOldEvent',
                );
                errorEvent = event;
                break;
              } else {
                console.log('[beatHr/changeEventDuration]-OVERLAP-sametype-intersect', event);
                //* Giao nhau
                if (
                  eventType === TYPE_RHYTHM_EVENT_ENUM.AFIB ||
                  eventType === TYPE_RHYTHM_EVENT_ENUM.ARTIFACT
                ) {
                  linkEvents.push(event);
                }
                //* Đổi start stop của event mới là hợp của cả 2 event
                updatedEvent.start = moment(Math.min(eventStart, newEventStart)).toISOString();
                updatedEvent.stop = moment(Math.max(eventStop, newEventStop)).toISOString();
              }
            } else if (
              (LIST_EVENT_SKIP_ON_RUN_EVENT.includes(eventType) &&
                LIST_RUN_EVENT.includes(updatedEvent.type)) ||
              (LIST_EVENT_SKIP_ON_RUN_EVENT.includes(updatedEvent.type) &&
                LIST_RUN_EVENT.includes(eventType))
            ) {
              //* VT/SVT không cần check overlap cho 1 số event type khác
              continue;
            } else if (
              (!isNewEventContainOldEvent &&
                updatedEvent.type === TYPE_RHYTHM_EVENT_ENUM.SVT &&
                eventType === TYPE_BEAT_EVENT_ENUM.SVE_RUN) ||
              (updatedEvent.type === TYPE_RHYTHM_EVENT_ENUM.VT &&
                eventType === TYPE_BEAT_EVENT_ENUM.VE_RUN)
            ) {
              if (isNewEventInsideOldEvent) {
                errorEvent = event;
                break;
              }
              //* Giao nhau
              linkEvents.push(event);
              //* Đổi start stop của event mới là hợp của cả 2 event
              updatedEvent.start = moment(Math.min(eventStart, newEventStart)).toISOString();
              updatedEvent.stop = moment(Math.max(eventStop, newEventStop)).toISOString();
            } else if (RHYTHM_EVENT_LIST_CAN_EDIT.includes(eventType)) {
              //* Mới chứa cũ khác loại
              if (isNewEventContainOldEvent) {
                console.log(
                  '[beatHr/changeEventDuration]-OVERLAP-difftype-isNewEventContainOldEvent',
                );
                if (
                  eventType === TYPE_RHYTHM_EVENT_ENUM.AFIB ||
                  eventType === TYPE_RHYTHM_EVENT_ENUM.ARTIFACT
                ) {
                  linkEvents.push(event);
                }
              } else if (isNewEventInsideOldEvent) {
                //* Mới trong cũ khác loại
                console.log(
                  '[beatHr/changeEventDuration]-OVERLAP-difftype-isNewEventInsideOldEvent',
                );
                if (
                  updatedEvent.type === TYPE_RHYTHM_EVENT_ENUM.AFIB &&
                  eventType === TYPE_RHYTHM_EVENT_ENUM.ARTIFACT
                ) {
                  errorMessages = ADD_AFIB_TO_ARTIFACT_ERROR_MESSAGE;
                  break;
                } else if (
                  updatedEvent.type === TYPE_RHYTHM_EVENT_ENUM.ARTIFACT &&
                  eventType === TYPE_RHYTHM_EVENT_ENUM.AFIB
                ) {
                  if (activeButton !== TYPE_RHYTHM_EVENT_ENUM.ARTIFACT) {
                    errorMessages = ADD_ARTIFACT_TO_AFIB_ERROR_MESSAGE;
                  }
                  break;
                }
                errorEvent = event;
                break;
              } else {
                if (
                  eventType !== TYPE_RHYTHM_EVENT_ENUM.AFIB ||
                  eventType !== TYPE_RHYTHM_EVENT_ENUM.ARTIFACT
                ) {
                  continue;
                }
                console.log('[beatHr/changeEventDuration]-OVERLAP-difftype-intersect');
                let currentEventStart = event.start;
                let currentEventStop = event.stop;
                //* Giao nhau khác loại
                //* nếu artifact event sau khi cắt chỉ còn 1 beat => xét duration nếu lớn hơn 10s thì cắt được, nếu nhỏ hơn 10s thì delete artifact event
                //* nếu afib,vt,svt,1st,2nd,3rd event sau khi cắt chỉ còn 1 beat => xóa luôn
                //* nếu tachy,brady,pause event sau khi cắt chỉ còn 1 beat => xoá luôn.
                if (eventStart < newEventStart && newEventStart < eventStop) {
                  currentEventStop = updatedEvent.start;
                }
                if (eventStart < newEventStop && newEventStop < eventStop) {
                  currentEventStart = updatedEvent.stop;
                }

                const { mergeBeatCount } = await getBeatCountAddUpdateEvents({
                  start: currentEventStart,
                  stop: currentEventStop,
                  samplingFrequency: ecgDataMap.samplingFrequency,
                  localBeatChanges,
                  ecgDataMap,
                });
                const newDuration = getEventDuration(currentEventStart, currentEventStop);
                const newDurationInSec = getEventDurationInSecond(
                  currentEventStart,
                  currentEventStop,
                );
                //* Không có beat -> xóa event
                if (eventType === TYPE_RHYTHM_EVENT_ENUM.AFIB) {
                  if (newDurationInSec >= 5) {
                    linkEvents.push({
                      ...event,
                      start: currentEventStart,
                      stop: currentEventStop,
                      duration: newDuration,
                      canChangeDuration: true,
                    });
                  } else {
                    linkEvents.push(event);
                  }
                } else if (mergeBeatCount === 1) {
                  // getEventDuration (Utils/aiUtils.ts) returns string | number depending on duration - pre-existing.
                  if ((newDuration as any) >= 10 && eventType === TYPE_RHYTHM_EVENT_ENUM.ARTIFACT) {
                    //* Tạo được
                    linkEvents.push({
                      ...event,
                      start: currentEventStart,
                      stop: currentEventStop,
                      duration: newDuration,
                      canChangeDuration: true,
                    });
                  } else {
                    //* Xóa
                    linkEvents.push(event);
                  }
                } else if (mergeBeatCount === 0) {
                  //* Xóa
                  linkEvents.push(event);
                } else {
                  //* Mảng beat lớn hơn 1
                  //* Tạo được
                  linkEvents.push({
                    ...event,
                    start: currentEventStart,
                    stop: currentEventStop,
                    duration: newDuration,
                    canChangeDuration: true,
                  });
                }
              }
            }
          }
        }
      }
      if (errorEvent) {
        handleSetError(generateUpdateEventInOtherEventErrorMessage(errorEvent, timezoneOffset));
        return;
      }
      if (errorMessages) {
        handleSetError(errorMessages);
        return;
      }
      updatedEvent.startTimeEpoch = moment(updatedEvent.start).startOf('minutes').valueOf();
      updatedEvent.stopTimeEpoch = moment(updatedEvent.stop).endOf('minutes').valueOf();
      updatedEvent.duration = getEventDuration(updatedEvent.start, updatedEvent.stop);
      updatedEvent.isChangeDuration = true;
      const clonedEventNews = _.cloneDeep(getEventNews());
      const clonedEventChanges = _.cloneDeep(getEventChanges());
      console.log('[beatHr/changeEventDuration]-linkevents', linkEvents);
      if (linkEvents.length) {
        linkEvents.forEach((x) => {
          if (x.isNew) {
            _.remove(clonedEventNews, (eventNew) => eventNew.id === x.id);
          } else if (x.canChangeDuration) {
            const foundEventChanged = clonedEventChanges.find(
              (eventChange) => eventChange.idEvent === x.idEvent,
            );
            if (foundEventChanged) {
              _.assign(foundEventChanged, {
                start: x.start,
                stop: x.stop,
                duration: x.duration,
              });
            } else {
              delete x.canChangeDuration;
              clonedEventChanges.push(x);
            }
          } else {
            const foundEventChanged = clonedEventChanges.find(
              (eventChange) => eventChange.idEvent === x.idEvent,
            );
            if (foundEventChanged) {
              _.assign(foundEventChanged, { type: TYPE_BEAT_ENUM.DELETED, oldType: x.oldType });
            }
            // else {
            //   clonedEventChanges.push({
            //     ...x,
            //     type: TYPE_BEAT_ENUM.DELETED,
            //     oldType: x.oldType,
            //   });
            // }
          }
        });
        updatedEvent.linkEvents = linkEvents.filter((x) => !x.isNew);
        const foundEventChange = clonedEventChanges.find(
          (eventChange) => eventChange.idEvent === updatedEvent.idEvent,
        );
        if (foundEventChange) {
          _.assign(foundEventChange, updatedEvent);
        } else {
          clonedEventChanges.push(updatedEvent);
        }
      } else {
        const foundEventChange = clonedEventChanges.find(
          (eventChange) => eventChange.idEvent === updatedEvent.idEvent,
        );
        if (foundEventChange) {
          _.assign(foundEventChange, updatedEvent);
        } else {
          clonedEventChanges.push(updatedEvent);
        }
      }
      // Perform grouping event action applicable to undo/redo
      setEventOptions((prevOptions: any) => ({
        ...prevOptions,
        eventNewsState: clonedEventNews,
        eventChangesState: clonedEventChanges,
      }));

      setEventChanges(clonedEventChanges);
      setEventNews(clonedEventNews);
      // selectedStripState (Task 7) is typed IEvent | IBeat | IEcgBookmark; idEvent doesn't exist on
      // IBeat/IEcgBookmark - pre-existing gap.
      if (updatedEvent?.idEvent === (getSelectedStrip() as any)?.idEvent) {
        setSelectedStrip(updatedEvent);
      }
      onClickCancel();
    } catch (error) {
      logError('Failed to change event duration: ', error);
      toastrError(error.message, 'Error');
      setState({ isSaving: false });
    }
  };

  /**
   * Validates the four duration pickers before handing the edit to the persistence path. Flags every empty field and a start date falling after the end date as invalid, aborts on any error, closes the modal untouched when neither boundary was actually picked, and otherwise runs the duration change.
   *
   * @returns Nothing.
   */
  const onClickSave = () => {
    setState({ isSaving: true });
    const clonedErrorObject = { ...state.errorObject };

    if (!state.startDate) {
      _.assign(clonedErrorObject, { startDate: 'Invalid value' });
    } else {
      delete clonedErrorObject.startDate;
    }

    if (!state.endDate) {
      _.assign(clonedErrorObject, { endDate: 'Invalid value' });
    } else {
      delete clonedErrorObject.endDate;
    }

    if (!state.startTime) {
      _.assign(clonedErrorObject, { startTime: 'Invalid value' });
    } else {
      delete clonedErrorObject.startTime;
    }

    if (!state.endTime) {
      _.assign(clonedErrorObject, { endTime: 'Invalid value' });
    } else {
      delete clonedErrorObject.endTime;
    }

    if (state.startDate && state.startTime && state.endDate && state.endTime) {
      if (state.startDate.isAfter(state.endDate)) {
        _.assign(clonedErrorObject, {
          startDate: 'Invalid value',
          endDate: 'Invalid value',
          startTime: 'Invalid value',
          endTime: 'Invalid value',
        });
      } else {
        delete clonedErrorObject.startDate;
        delete clonedErrorObject.endDate;
        delete clonedErrorObject.startTime;
        delete clonedErrorObject.endTime;
      }
    }

    if (!_.isEmpty(clonedErrorObject)) {
      setState({ errorObject: clonedErrorObject, isSaving: false });
      return;
    }
    if (!pickedEndDate && !pickedStartDate) {
      onClickCancel();
      return;
    }
    handleChangeEventDuration();
  };

  useEffect(() => {
    if (!_.isEmpty(selectedEvent)) {
      const convertedStartMoment = convertToMoment(selectedEvent?.start, timezone);
      const convertedEndMoment = convertToMoment(selectedEvent?.stop, timezone);
      setState({
        startDate: convertedStartMoment,
        endDate: convertedEndMoment,
        startTime: convertedStartMoment,
        endTime: convertedEndMoment,
        errorObject: {},
      });
    }
  }, [selectedEvent]);

  useUpdateEffect(() => {
    let convertedStartDateMoment;
    let convertedEndDateMoment;
    if (pickedStartDate) {
      convertedStartDateMoment = convertToMoment(pickedStartDate, timezone);
    } else {
      convertedStartDateMoment = convertToMoment(selectedEvent?.start, timezone);
    }
    if (pickedEndDate) {
      convertedEndDateMoment = convertToMoment(pickedEndDate, timezone);
    } else {
      convertedEndDateMoment = convertToMoment(selectedEvent?.stop, timezone);
    }
    setState({
      startDate: convertedStartDateMoment,
      startTime: convertedStartDateMoment,
      endDate: convertedEndDateMoment,
      endTime: convertedEndDateMoment,
      errorObject: {},
    });
  }, [pickedStartDate, pickedEndDate]);

  useEffect(() => {
    if (
      !controlModal.isShowChangeDurationSelectedInfo ||
      !controlModal.isShowChangeDurationButtonType
    ) {
      managerAPI.cancelStore(KEY_CANCEL.API_MODAL);
    }
  }, [controlModal.isShowChangeDurationSelectedInfo, controlModal.isShowChangeDurationButtonType]);

  return {
    state,
    onChange,
    onClickCancel,
    onClickSave,
  };
};

export default useChangeEventDuration;
